import { Injectable } from '@angular/core';
import { AppTextService } from '@fi-shared/app-text';
import {
    IBalooSectionLogItem,
    IBalooSyncLog,
    IBalooSyncLogItem,
} from '@fi-app/external-platform-integration/baloo/baloo';
import { AccessRightType, ObjectTypes } from '@fi-shared/types';
import { BalooSyncLogItemType } from '@fi-app/external-platform-integration/baloo/baloo-sync-log-item-type';
import { BalooItemStatusType } from '@fi-app/external-platform-integration/baloo/baloo-item-status-type';
import { BalooSyncResultType } from '@fi-app/external-platform-integration/baloo/baloo-sync-result-type';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { ActionFailureReasonCode } from '@fi-shared/types/action-failed-reason-code';
import { BalooSyncLogCategoryType } from '@fi-app/external-platform-integration/baloo/baloo-sync-log/baloo-sync-log-category-type';
import {
    SyncSectionLabelColorClass,
    UnitRoleLogSummaryItems,
    UserLogSummaryItems,
} from '@fi-app/external-platform-integration/baloo/baloo-sync-log-summary-items.enum';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { BalooSyncLogDetailsDialogComponent } from '@fi-app/external-platform-integration/baloo/baloo-sync-log/baloo-sync-log-details-dialog/baloo-sync-log-details-dialog.component';
import { FiDialogsService } from '@fi-shared/fi-dialogs';

@Injectable()
export class BalooHelperService {
    constructor(
        private appTextService: AppTextService,
        private customerDataService: FiCustomerDataService,
        private fiFormatDateService: FiFormatDateService,
        private fiDialogsService: FiDialogsService,
    ) {}

    getCategoryName(logCategoryType: BalooSyncLogCategoryType, objectType: ObjectTypes): string {
        switch (logCategoryType) {
            case BalooSyncLogCategoryType.Added:
                return this.appTextService.get(
                    objectType !== ObjectTypes.User ? 'common.added' : 'baloo.added_to_baloo',
                );
            case BalooSyncLogCategoryType.Updated:
                return this.appTextService.get('common.updated');
            case BalooSyncLogCategoryType.Reactivated:
                return this.appTextService.get(
                    objectType !== ObjectTypes.User ? 'common.reactivated' : 'baloo.reactivated_on_baloo',
                );
            case BalooSyncLogCategoryType.Deactivated:
                return this.appTextService.get(
                    objectType !== ObjectTypes.User ? 'common.deactivated' : 'baloo.deactivated_on_baloo',
                );
            case BalooSyncLogCategoryType.AddedToGroup: // only for user
                return this.appTextService.get('baloo.added_to_groups_on_baloo');
            case BalooSyncLogCategoryType.RemovedFromGroup: // only for user
                return this.appTextService.get('baloo.removed_from_groups_on_baloo');
            case BalooSyncLogCategoryType.Failed:
                return this.appTextService.get('failed');
        }
        throw new Error(`getCategoryName: unknown baloo sync log category type ${logCategoryType}`);
    }

    //region manual import summary log sections
    getSummaryLogText(syncLog: Readonly<IBalooSyncLog>): string {
        const textParts: string[] = [];

        this.getSummaryItemsByObjectType(syncLog.ObjectType).forEach((nonUserLogSummaryItem: IBalooSectionLogItem) => {
            const count: number = syncLog[nonUserLogSummaryItem.propertyName];
            const groupText: string = this.appTextService
                .get(nonUserLogSummaryItem.getItemGroupTextId(count, syncLog.ObjectType))
                .toLowerCase();

            textParts.push(
                this.getSummaryLogSectionText(
                    nonUserLogSummaryItem.textId,
                    count,
                    groupText,
                    nonUserLogSummaryItem.labelColorClass,
                ),
            );
        });

        return textParts.join(' ');
    }

    private getSummaryItemsByObjectType(objectType: ObjectTypes): IBalooSectionLogItem[] {
        if (objectType === ObjectTypes.User) return UserLogSummaryItems;
        if (objectType === ObjectTypes.BusinessUnit || objectType === ObjectTypes.Role) return UnitRoleLogSummaryItems;
    }

    private getSummaryLogSectionText(
        textId: string,
        count: number,
        objectName: string,
        colorClass: SyncSectionLabelColorClass,
    ): string {
        return `
            <span class="sync-log-section-label ${colorClass}">${this.appTextService.get(textId, {
                '{n}': count,
                '{object_name}': objectName,
            })}</span>
        `;
    }
    //endregion

    getLogText(logItem: Readonly<IBalooSyncLogItem>): string {
        const logItemType = logItem.LogItemType;
        const customerDisplayName = this.customerDataService.customerData.DisplayName;
        const objectType = logItem.ObjectType;
        const textParts: string[] = [];
        if (objectType === ObjectTypes.BusinessUnit || objectType === ObjectTypes.Role) {
            if (logItemType === BalooSyncLogItemType.BalooCreated) {
                textParts.push(`${logItem.BalooNewName}`);
            } else if (logItemType === BalooSyncLogItemType.BalooUpdated) {
                textParts.push(
                    this.appTextService.get('baloo.log_group_name_changed', {
                        '{new_name}': logItem.BalooNewName,
                        '{old_name}': logItem.BalooOldName,
                    }),
                );
            } else if (logItemType === BalooSyncLogItemType.BalooReactivated) {
                textParts.push(
                    this.appTextService.get('baloo.log_group_reactivated', {
                        '{name}': logItem.BalooNewName,
                        '{display_name}': customerDisplayName,
                    }),
                );
            } else if (logItemType === BalooSyncLogItemType.BalooDeactivated) {
                if (logItem.ItemStatuses.indexOf(BalooItemStatusType.Deactivated)) {
                    textParts.push(
                        this.appTextService.get('baloo.log_group_deactivated', {
                            '{name}': logItem.BalooNewName,
                            '{display_name}': customerDisplayName,
                        }),
                    );
                } else if (logItem.ItemStatuses.indexOf(BalooItemStatusType.Deleted)) {
                    textParts.push(
                        this.appTextService.get('baloo.log_group_deactivated_item_deleted', {
                            '{name}': logItem.BalooNewName,
                            '{display_name}': customerDisplayName,
                        }),
                    );
                }
            } else if (logItemType === BalooSyncLogItemType.Error) {
                textParts.push(`${logItem.BalooNewName} - ${this.getErrorMessage(logItem.ErrorCode)}`);
            }
        } else if (objectType === ObjectTypes.User) {
            if (logItemType === BalooSyncLogItemType.BalooCreated) {
                const balooRoleName = this.getBalooMemberManagerRoleName(logItem.BalooNewIsManager);
                textParts.push(
                    this.appTextService.get('baloo.user_added_as', {
                        '{user_name}': logItem.BalooNewName,
                        '{baloo_role}': balooRoleName,
                    }),
                );
                textParts.push(this.getUserDetailsText(logItem));
            } else if (logItemType === BalooSyncLogItemType.BalooUpdated) {
                if (logItem.BalooNewName !== logItem.BalooOldName) {
                    textParts.push(
                        this.appTextService.get('baloo.user_name_updated', {
                            '{user_name}': logItem.BalooNewName,
                        }),
                    );
                }
                if (logItem.BalooNewName !== logItem.BalooOldEmail) {
                    textParts.push(
                        this.appTextService.get('baloo.user_email_updated', {
                            '{user_name}': logItem.BalooNewName,
                            '{email}': logItem.BalooNewEmail,
                        }),
                    );
                }
                if (logItem.BalooNewIsManager !== logItem.BalooOldIsManager) {
                    const balooRoleName = this.getBalooMemberManagerRoleName(logItem.BalooNewIsManager);
                    textParts.push(
                        this.appTextService.get('baloo.user_role_updated', {
                            '{user_name}': logItem.BalooNewName,
                            '{baloo_role}': balooRoleName,
                        }),
                    );
                }
            } else if (logItemType === BalooSyncLogItemType.BalooReactivated) {
                textParts.push(
                    this.appTextService.get('baloo.user_reactivated', {
                        '{user_name}': logItem.BalooNewName,
                        '{display_name}': customerDisplayName,
                    }),
                );
            } else if (logItemType === BalooSyncLogItemType.BalooDeactivated) {
                if (logItem.ItemStatuses.indexOf(BalooItemStatusType.Deleted) !== -1) {
                    textParts.push(
                        this.appTextService.get('baloo.user_deleted', {
                            '{user_name}': logItem.BalooNewName,
                            '{display_name}': customerDisplayName,
                        }),
                    );
                    textParts.push(this.getUserDetailsText(logItem));
                } else if (logItem.ItemStatuses.indexOf(BalooItemStatusType.Deactivated) !== -1) {
                    textParts.push(
                        this.appTextService.get('baloo.user_deactivated', {
                            '{user_name}': logItem.BalooNewName,
                            '{display_name}': customerDisplayName,
                        }),
                    );
                    textParts.push(this.getUserDetailsText(logItem));
                } else if (logItem.ItemStatuses.indexOf(BalooItemStatusType.NoAccessToBaloo) !== -1) {
                    textParts.push(
                        this.appTextService.get('baloo.user_belongs_to_role', {
                            '{user_name}': logItem.BalooNewName,
                            '{baloo_role}': this.getBalooModuleAccessName(AccessRightType.UnAvailable),
                        }),
                    );
                    textParts.push(this.getUserDetailsText(logItem));
                }
            } else if (logItemType === BalooSyncLogItemType.BalooAddedToGroup) {
                if (logItem.GroupsMemberAdded.length) {
                    textParts.push(
                        this.appTextService.get('baloo.user_added_as_to', {
                            '{user_name}': logItem.BalooNewName,
                            '{baloo_role}': this.getBalooMemberManagerRoleName(false),
                            '{group_names}': logItem.GroupsMemberAdded.map((item) => item.GroupName).join(', '),
                        }),
                    );
                    textParts.push(this.getUserDetailsText(logItem));
                }
                if (logItem.GroupsManagerAdded.length) {
                    textParts.push(
                        this.appTextService.get('baloo.user_added_as_to', {
                            '{user_name}': logItem.BalooNewName,
                            '{baloo_role}': this.getBalooMemberManagerRoleName(true),
                            '{group_names}': logItem.GroupsManagerAdded.map((item) => item.GroupName).join(', '),
                        }),
                    );
                    textParts.push(this.getUserDetailsText(logItem));
                }
            } else if (logItemType === BalooSyncLogItemType.BalooRemovedFromGroup) {
                textParts.push(
                    this.appTextService.get('baloo.user_removed_from', {
                        '{user_name}': logItem.BalooNewName,
                        '{group_names}': logItem.GroupsRemoved.map((item) => item.GroupName).join(', '),
                    }),
                );
                textParts.push(this.getUserDetailsText(logItem));
            } else if (logItemType === BalooSyncLogItemType.Error) {
                if (logItem.Result === BalooSyncResultType.MultipleEmailsInChainformation) {
                    textParts.push(
                        `${logItem.BalooNewName} - ${this.appTextService.get('error.multiple_emails_in_chainformation')}`,
                    );
                    textParts.push(this.getUserDetailsText(logItem));
                }
            }
        }
        return textParts.length
            ? textParts.join('<br>')
            : `<span class="red-status-color">!!! ${JSON.stringify(logItem)}</span><br><br>`; // for test

        // todo
        //throw new Error(`getLogText: unknown logItem data "${JSON.stringify(logItem)}"`);
    }

    getSyncLogDownloadFileName(createdOn: string): string {
        return `${this.appTextService.get('baloo.sync_log')} ${this.fiFormatDateService.formatDate(
            createdOn,
            'date-time',
            { showToday: false },
        )}`;
    }

    private getBalooMemberManagerRoleName(isManager: boolean): string {
        const balooModuleAccessName: string = this.getBalooModuleAccessName(
            isManager ? AccessRightType.Share : AccessRightType.Read,
        ).toLowerCase();
        const spanClass: string = isManager ? 'orange-status-color' : 'green-status-color';
        return `<span class="${spanClass}">${balooModuleAccessName}</span>`;
    }

    private getBalooModuleAccessName(balooAccessRightType: AccessRightType) {
        if (balooAccessRightType === AccessRightType.UnAvailable) return this.appTextService.get('common.disabled');
        if (balooAccessRightType === AccessRightType.Read) return this.appTextService.get('baloo_access_level.member');
        if (balooAccessRightType === AccessRightType.Share)
            return this.appTextService.get('baloo_access_level.manager');
        if (balooAccessRightType === AccessRightType.Admin)
            return this.appTextService.get('baloo_access_level.administrator');

        throw new Error(`getBalooModuleAccessName: unknown access right type ${balooAccessRightType}`);
    }

    private getUserDetailsText(logItem: Readonly<IBalooSyncLogItem>): string {
        const roleNames: string = logItem.Roles.length
            ? logItem.Roles.map((item) => item.Name).join(', ')
            : this.appTextService.get('none').toLowerCase();
        const unitNames: string = logItem.Roles.length
            ? logItem.BusinessUnits.map((item) => item.Name).join(', ')
            : this.appTextService.get('none').toLowerCase();
        const unitsText = `<b>${this.appTextService.get('common.units')}</b>: ${unitNames}`;
        const rolesText = `<b>${this.appTextService.get('common.roles')}</b>: ${roleNames}`;
        return `<span class="user-details">${logItem.BalooNewEmail}, ${unitsText}, ${rolesText}</span>`;
    }

    private getErrorMessage(errorCode: ActionFailureReasonCode): string {
        return this.appTextService.get(
            errorCode === ActionFailureReasonCode.Baloo_ExternalRequestError
                ? 'baloo.external_request_error'
                : 'error.unknown',
        );
    }

    openDetailedLogDialog(log: IBalooSyncLog) {
        this.fiDialogsService.custom({
            hideFooter: true,
            hideHeader: true,
            contentComponentRef: BalooSyncLogDetailsDialogComponent,
            contentComponentData: log,
            disableHeightOverflowCheck: true,
            config: {
                maxWidth: '700px',
                width: '80%',
                panelClass: ['fi-rounded-dialog-panel', 'fi-baloo-sync-log-dialog-panel'],
                autoFocus: false,
            },
        });
    }
}
